import { type ReactNode } from 'react';
import { cn } from '../lib/cn';
import { buildGridTemplate, type ModernColumn } from './ModernDataTableShell';

/**
 * ModernTableRow — one row that lines up with ModernDataTableShell's columns.
 * Pass the same `columns` so the grid template matches, plus a `cells` map
 * keyed by column key. Selection + click are wired by the caller to existing
 * app actions.
 */
export function ModernTableRow({
  columns,
  cells,
  selectable = false,
  selected = false,
  onSelectChange,
  onClick,
  className,
}: {
  columns: ModernColumn[];
  cells: Record<string, ReactNode>;
  selectable?: boolean;
  selected?: boolean;
  onSelectChange?: (selected: boolean) => void;
  onClick?: () => void;
  className?: string;
}) {
  const gridTemplate = buildGridTemplate(columns, selectable);
  const interactive = Boolean(onClick);

  return (
    <div
      role="row"
      onClick={onClick}
      tabIndex={interactive ? 0 : undefined}
      onKeyDown={
        interactive
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick?.();
              }
            }
          : undefined
      }
      className={cn(
        'grid items-center gap-3 px-4 py-3 text-[13px] text-foreground transition-colors',
        interactive &&
          'cursor-pointer hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring',
        selected && 'bg-primary/[0.05]',
        className,
      )}
      style={{ gridTemplateColumns: gridTemplate }}
    >
      {selectable && (
        <input
          type="checkbox"
          checked={selected}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => onSelectChange?.(e.target.checked)}
          className="h-3.5 w-3.5 rounded border-border accent-[hsl(var(--primary))]"
          aria-label="Select row"
        />
      )}
      {columns.map((col) => (
        <div
          key={col.key}
          className={cn(
            'min-w-0 truncate',
            col.align === 'right' && 'text-right tabular-nums',
            col.align === 'center' && 'text-center',
            col.hideOnMobile && 'hidden sm:block',
          )}
        >
          {cells[col.key] ?? <span className="text-muted-foreground">—</span>}
        </div>
      ))}
    </div>
  );
}
